import { AbstractMesh, ArcRotateCamera, Engine, GlowLayer, Mesh, Scene, Vector3 } from "@babylonjs/core";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Fire } from "./fire";
import { BoundingBox, TransformNode } from "@babylonjs/core";

export class ViewerScene extends Scene {
    private static DEFAULT_SCENE_COLOR: Color4 = new Color4(0, 0, 0, 0);

    private canvas: HTMLCanvasElement;

    // Lights
    private mainLight: DirectionalLight;
    private fillLight: DirectionalLight;
    private mainShadowGenerator: ShadowGenerator;

    // Effects
    private glowLayer?: GlowLayer;

    // Objects
    private fire: Fire;
    private poeleBoundingBox: BoundingBox;

    constructor(engine: Engine, canvas: HTMLCanvasElement){
        super(engine);
        this.canvas = canvas;
    }

    public initialize(){
        // This creates and positions a free camera (non-mesh)

        // Calculates the average center of the mesh
        let min = new Vector3(0, 0, 0);
        let max = new Vector3(0, 0, 0);

        let root = this.getNodeByName('__root__') as unknown as TransformNode;
        root.computeWorldMatrix(true);
        root.getChildMeshes(false)
        .filter((node) => { return !(node.name.includes("Fire") || node.name.includes("Inte") || node.name.includes("Vitre") || node.name.includes("Buche") || node.name.includes("Poignee")); })
        .forEach(node => {
            let mesh = node as unknown as Mesh;
            mesh.computeWorldMatrix(true);
            if (mesh.hasBoundingInfo) {
                mesh.getBoundingInfo().update(mesh.getWorldMatrix());
                let meshBox = mesh.getBoundingInfo().boundingBox;

                if (meshBox.minimumWorld.x <= min.x) min.x = meshBox.minimumWorld.x;
                if (meshBox.minimumWorld.y <= min.y) min.y = meshBox.minimumWorld.y;
                if (meshBox.minimumWorld.z <= min.z) min.z = meshBox.minimumWorld.z;

                if (meshBox.maximumWorld.x >= max.x) max.x = meshBox.maximumWorld.x;
                if (meshBox.maximumWorld.y >= max.y) max.y = meshBox.maximumWorld.y;
                if (meshBox.maximumWorld.z >= max.z) max.z = meshBox.maximumWorld.z;
            }
        });

        this.poeleBoundingBox = new BoundingBox(min, max);
        console.log(this.poeleBoundingBox.centerWorld);
        root.position = root.position.subtract(this.poeleBoundingBox.centerWorld);

        let target = new Vector3(0, this.poeleBoundingBox.centerWorld.y, 0);
        let diagonalWorld = max.subtract(min);

        const arcCamera = new ArcRotateCamera("camera1", Math.PI, Math.PI / 2.7, 0.6 + diagonalWorld.length(), target, this);
        arcCamera.attachControl(this.canvas, true);
        arcCamera.wheelPrecision = 100;
        arcCamera.pinchPrecision = 1000;

        arcCamera.angularSensibilityX = 2000;
        arcCamera.angularSensibilityY = 2000;

        arcCamera.lowerRadiusLimit = 1;
        arcCamera.upperRadiusLimit = 3;
        arcCamera.upperBetaLimit = 1.70;

        arcCamera.minZ = 0.1;
        arcCamera.maxZ = 30;
        console.log(arcCamera.inertia);

        //arcCamera.panningAxis = Vector3.Zero();
        arcCamera.useFramingBehavior = false;
        arcCamera.checkCollisions = false;

        this.mainLight = new DirectionalLight("mainLight", new Vector3(1.0, -2.0, -1.0), this);
        this.mainLight.intensity = 12;
        this.mainLight.shadowEnabled = true;
        this.mainLight.shadowMinZ = 0.1;
        this.mainLight.shadowMaxZ = 3;
        this.mainShadowGenerator = new ShadowGenerator(256, this.mainLight);
        this.mainShadowGenerator.useBlurCloseExponentialShadowMap = true;

        this.fillLight = new DirectionalLight("fillLight", new Vector3(-1.0, -1.0, 3.0), this);
        this.fillLight.position = new Vector3(0,1.5,-2);
        this.fillLight.intensity = 2;

        this.clearColor = ViewerScene.DEFAULT_SCENE_COLOR;


        this.fire = new Fire(this);

        this.setupEnvironment();
        this.setupGlowLayer();
    }

    private setupEnvironment(): void {
        this.createDefaultEnvironment({
            createSkybox: false,
            createGround: false,
        });
        
        const ground = Mesh.CreatePlane('ground', 10, this);
        ground.rotation = new Vector3(Math.PI / 2, 0, 0);
        ground.position = new Vector3(0, this.poeleBoundingBox.minimumWorld.y, 0);
        const material = this.getMaterialByName('shadowOnly_base');
        ground.material = material;
        ground.receiveShadows = true;

        // We can only get the shadows from ONE light (even if the other lights in the scene aren't casting shadows)
        // So we have to make sure the ground isn't affected by any other light than mainLight
        // TODO - The fix is comming from BJS: https://github.com/BabylonJS/Babylon.js/pull/14207
        this.fillLight.excludedMeshes.push(ground);
    }

    private setupGlowLayer(): void{
        this.glowLayer = new GlowLayer("glow", this);
        this.glowLayer.intensity = 1;
        this.glowLayer.blurKernelSize = 16;

        // set up material to use glow layer
        this.glowLayer.referenceMeshToUseItsOwnMaterial(this.fire.getMesh());

        //enable glow mask to render only emissive into glow layer, and then disable glow mask
        this.glowLayer.onBeforeRenderMeshToEffect.add(() => {
            this.fire.enableGlowMask();
        });
        this.glowLayer.onAfterRenderMeshToEffect.add(() => {
            this.fire.disableGlowMask();
        });
    }

    public addShadowCaster(mesh: AbstractMesh, includeDescendants: boolean) {
        this.mainShadowGenerator.addShadowCaster(mesh, includeDescendants);
    }
}